/**
 * Management Toolbar Component
 * 
 * Reusable toolbar component for management modules.
 * Includes search functionality, filtering, and add button.
 * 
 * @module ManagementToolbar
 */

import React from 'react';
import { Box } from '@mui/material';
import { Button, Search } from '@voicecare.ai/ui-library';
import { useTheme } from '../../ThemeContext';
import { useDetectMyRole } from '../../custom-hooks';
import FilterDropdown from './FilterDropdown';
import ThemedTooltip from './ThemedTooltip';
import { MODULE_PERMISSIONS } from '../../constants/filter-constants';

/**
 * ManagementToolbar Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.module - Module name ('facility', 'user', 'provider')
 * @param {string} props.searchQuery - Current search query
 * @param {Function} props.onSearchChange - Search query change handler
 * @param {string} props.searchPlaceholder - Placeholder text for search input
 * @param {Array} props.filterGroups - Filter group configurations
 * @param {Object} props.selectedFilters - Currently selected filters
 * @param {Function} props.onFiltersChange - Filter change handler
 * @param {Function} props.onAdd - Add button click handler
 * @param {string} props.addButtonLabel - Label for add button
 * @param {boolean} [props.showAddButton=true] - Whether to show add button
 * @param {boolean} [props.showFilters=true] - Whether to show filter dropdown
 * @param {boolean} [props.showSearch=true] - Whether to show search input
 * @param {Object} [props.sx] - Additional styling
 * @returns {JSX.Element} ManagementToolbar component
 * 
 * @example
 * <ManagementToolbar
 *   module="facility"
 *   searchQuery={query}
 *   onSearchChange={setQuery}
 *   searchPlaceholder="Search facilities..."
 *   filterGroups={filterGroups}
 *   selectedFilters={selectedFilters}
 *   onFiltersChange={handleFiltersChange}
 *   onAdd={handleAdd}
 *   addButtonLabel="Add Facility"
 * />
 */
const ManagementToolbar = ({
  module,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  filterGroups = [],
  selectedFilters = {},
  onFiltersChange,
  onAdd,
  addButtonLabel,
  searchBarWidth = "100%",
  showAddButton = true,
  showFilters = true,
  showSearch = true,
  sx = {},
  ...otherProps
}) => {
  const { currentTheme, themeMode } = useTheme();
  const { role } = useDetectMyRole();

  /**
   * Checks if user has permission for the specified action
   * 
   * @param {string} action - Action to check permission for
   * @returns {boolean} Whether user has permission
   */
  const hasPermission = (action) => {
    const modulePermissions = MODULE_PERMISSIONS[module];
    if (!modulePermissions || !modulePermissions[action]) {
      return role === 'admin'; // Default to admin-only
    }
    return modulePermissions[action].includes(role);
  };

  const canCreate = hasPermission('create');

  /**
   * Gets tooltip text for add button based on permissions
   * 
   * @returns {string} Tooltip text
   */
  const getAddButtonTooltip = () => {
    if (canCreate) {
      return `Add new ${module} to the system`;
    }
    return `Administrator privileges required to add ${module} records`;
  };

  return (
    <Box 
      sx={{ 
        display: "flex", 
        width: "100%", 
        justifyContent: "space-between",
        alignItems: "center",
        ...sx 
      }} 
      {...otherProps}
    >
      {/* Left side - Search Input */}
      {showSearch ? (
        <Box sx={{ flex: 1, maxWidth: "80%" }}>
          <Search
            value={searchQuery}
            onChange={onSearchChange}
            themeMode={themeMode}
            currentTheme={currentTheme}
            style={{ width: searchBarWidth }}
            placeholder={searchPlaceholder}
            fontSize=".9rem"
          />
        </Box>
      ) : (
        <Box /> // Empty box to maintain space-between layout
      )}

      {/* Right side - Buttons Group */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {/* Filter Dropdown */}
        {showFilters && filterGroups.length > 0 && (
          <FilterDropdown
            filterGroups={filterGroups}
            selectedFilters={selectedFilters}
            onFiltersChange={onFiltersChange}
            placeholder="Filter"
            sx={{ minWidth: '120px' }}
          />
        )}

        {/* Add Button */}
        {showAddButton && (
          <ThemedTooltip 
            title={getAddButtonTooltip()}
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                onClick={canCreate ? onAdd : undefined}
                disabled={!canCreate}
                label={addButtonLabel}
                style={{ 
                  ml: 0, 
                  borderRadius: '6px',
                  cursor: canCreate ? 'pointer' : 'not-allowed'
                }}
              />
            </span>
          </ThemedTooltip>
        )}
      </Box>
    </Box>
  );
};

export default ManagementToolbar;
